import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';

const CookiePolicy = () => {
  const { user, userProfile, isAuthenticated } = useAuth();
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: true,
    marketing: false
  });

  const cookieSections = [
    {
      id: 'what-are-cookies',
      title: '1. What Are Cookies?',
      content: [
        {
          subtitle: 'Cookie Definition',
          text: 'Cookies are small text files that websites place on your computer or mobile device when you visit them. They allow websites to recognize your device and store information about your preferences or past actions, enhancing your browsing experience.'
        },
        {
          subtitle: 'How We Use Cookies',
          text: 'BardPlay uses cookies to improve your learning experience, remember your settings, track progress, and analyze how you use our platform. We are transparent about what data we collect and why we collect it.'
        }
      ]
    },
    {
      id: 'types-of-cookies',
      title: '2. Types of Cookies We Use',
      content: [
        {
          subtitle: 'Essential Cookies (Required)',
          text: 'These cookies are necessary for the basic functionality of BardPlay. They enable core features like user authentication, session management, security features, and page navigation. You cannot opt out of essential cookies while using our platform.'
        },
        {
          subtitle: 'Analytics Cookies (Optional)',
          text: 'Analytics cookies help us understand how students and educators interact with our platform. We use this data to identify popular features, detect usability issues, measure learning outcomes, and improve the overall user experience. These cookies do not personally identify you.'
        },
        {
          subtitle: 'Performance Cookies (Optional)',
          text: 'Performance cookies collect information about how you use our website, such as which pages you visit most often and whether you receive error messages. This helps us optimize platform performance and load times for all users.'
        }
      ]
    },
    {
      id: 'cookie-categories',
      title: '3. Cookie Categories',
      content: [
        {
          subtitle: 'Authentication & Security',
          text: 'Session tokens, login credentials encryption, two-factor authentication tokens, and CSRF protection cookies ensure your account remains secure and your session is maintained across pages.'
        },
        {
          subtitle: 'User Preferences',
          text: 'Settings for dark mode, language preferences, accessibility options, notification preferences, and customized dashboard layouts are stored to provide a personalized experience.'
        },
        {
          subtitle: 'Educational Progress Tracking',
          text: 'Cookies that track your learning progress including completed plays, quiz scores, achievement milestones, and bookmarked content help provide personalized recommendations and progress reports.'
        }
      ]
    },
    {
      id: 'third-party-cookies',
      title: '4. Third-Party Cookies',
      content: [
        {
          subtitle: 'Educational Service Providers',
          text: 'We work with trusted educational technology providers who may set their own cookies to deliver services like content delivery networks, authentication services, and learning analytics tools. These partners are bound by strict data protection agreements.'
        },
        {
          subtitle: 'Analytics Services',
          text: 'With your consent, we use analytics services to understand user behavior and improve our platform. These services may use cookies to track aggregated, anonymized data about user interactions.'
        }
      ]
    },
    {
      id: 'cookie-lifespan',
      title: '5. Cookie Lifespan',
      content: [
        {
          subtitle: 'Session Cookies',
          text: 'Session cookies are temporary and are deleted when you close your browser. They help maintain your login state and preferences during your browsing session.'
        },
        {
          subtitle: 'Persistent Cookies',
          text: 'Persistent cookies remain on your device for a set period (typically 30-365 days) or until you delete them. They help us remember your preferences and provide a seamless experience across visits.'
        }
      ]
    },
    {
      id: 'managing-cookies',
      title: '6. Managing Your Cookie Preferences',
      content: [
        {
          subtitle: 'Cookie Controls',
          text: 'You can manage your cookie preferences at any time through our cookie settings panel below. Changes to your preferences will take effect immediately and apply to all future sessions.'
        },
        {
          subtitle: 'Browser Settings',
          text: 'Most web browsers allow you to control cookies through browser settings. You can set your browser to refuse all cookies, accept only certain cookies, or notify you when a cookie is set. Note that disabling essential cookies may affect platform functionality.'
        }
      ]
    },
    {
      id: 'data-privacy',
      title: '7. Data Privacy & COPPA Compliance',
      content: [
        {
          subtitle: 'Student Privacy Protection',
          text: 'BardPlay is committed to protecting student privacy and complies with COPPA (Children\'s Online Privacy Protection Act) and FERPA (Family Educational Rights and Privacy Act). We obtain proper consent before collecting data from students under 13.'
        },
        {
          subtitle: 'Data Usage Transparency',
          text: 'We never sell student data to third parties. Cookies are used solely to enhance the educational experience, provide progress tracking, and improve platform functionality. All data collection practices are transparent and documented.'
        }
      ]
    },
    {
      id: 'cookie-updates',
      title: '8. Updates to This Policy',
      content: [
        {
          subtitle: 'Policy Changes',
          text: 'We may update this cookie policy to reflect changes in technology, legal requirements, or our services. We will notify you of significant changes via email or prominent platform notice.'
        },
        {
          subtitle: 'Effective Date',
          text: 'This cookie policy is effective as of December 12, 2025. Last updated: December 12, 2025.'
        }
      ]
    }
  ];

  const cookieTypes = [
    {
      name: 'Essential Cookies',
      required: true,
      description: 'Required for basic site functionality including authentication, security, and session management',
      examples: ['auth_token', 'session_id', 'csrf_token', 'user_preferences']
    },
    {
      name: 'Analytics Cookies',
      required: false,
      description: 'Help us understand how you use the platform and improve user experience',
      examples: ['_ga', '_gid', 'analytics_session', 'user_behavior']
    },
    {
      name: 'Marketing Cookies',
      required: false,
      description: 'Used to track visitors across websites for advertising purposes',
      examples: ['_fbp', 'conversion_tracking', 'retargeting']
    }
  ];

  const handleCookieToggle = (cookieType) => {
    if (cookieType !== 'essential') {
      setCookiePreferences(prev => ({
        ...prev,
        [cookieType]: !prev?.[cookieType]
      }));
    }
  };

  const handleSavePreferences = () => {
    // Save cookie preferences logic
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    alert('Cookie preferences saved successfully!');
  };

  return (
    <>
      <Helmet>
        <title>Cookie Policy - BardPlay</title>
        <meta name="description" content="Learn about how BardPlay uses cookies to enhance your learning experience and protect your privacy on our educational platform." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Header */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
                <Icon name="Cookie" size={20} className="text-primary" />
                <span className="text-primary font-semibold">Your Privacy Choices</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Cookie Policy</h1>
              <p className="text-lg text-white/70">
                Last Updated: December 12, 2025
              </p>
              <p className="text-white/60 mt-4 max-w-2xl mx-auto">
                This Cookie Policy explains how BardPlay uses cookies and similar technologies to enhance your learning experience and protect your privacy.
              </p>
            </div>
          </div>

          {/* Cookie Preference Manager */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name="Settings" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Cookie Preferences</h2>
                  <p className="text-white/70 text-sm">Manage your cookie settings and privacy preferences</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                {cookieTypes?.map((cookie, index) => (
                  <div 
                    key={index}
                    className="flex items-start justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-white">{cookie?.name}</h3>
                        {cookie?.required && (
                          <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-semibold rounded-full">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-white/70 text-sm mb-2">{cookie?.description}</p>
                      <p className="text-white/50 text-xs">
                        Examples: {cookie?.examples?.join(', ')}
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handleCookieToggle(cookie?.name?.toLowerCase()?.split(' ')?.[0])}
                        disabled={cookie?.required}
                        className={`relative w-14 h-8 rounded-full transition-colors ${
                          cookie?.required || cookiePreferences?.[cookie?.name?.toLowerCase()?.split(' ')?.[0]]
                            ? 'bg-primary' :'bg-white/20'
                        } ${cookie?.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div
                          className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                            cookie?.required || cookiePreferences?.[cookie?.name?.toLowerCase()?.split(' ')?.[0]]
                              ? 'translate-x-6' :'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={handleSavePreferences}
                className="w-full px-6 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Icon name="Check" size={20} />
                Save Cookie Preferences
              </button>
            </div>
          </div>

          {/* Cookie Policy Content */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="space-y-8">
              {cookieSections?.map((section, index) => (
                <div key={index} className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-8 border border-white/10">
                  <h2 className="text-2xl font-bold text-white mb-6">{section?.title}</h2>
                  <div className="space-y-6">
                    {section?.content?.map((item, idx) => (
                      <div key={idx}>
                        <h3 className="text-lg font-semibold text-white mb-2">{item?.subtitle}</h3>
                        <p className="text-white/70 leading-relaxed">{item?.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Information */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Info" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Questions About Cookies?</h3>
                  <p className="text-white/70 mb-4">
                    If you have questions about our cookie policy or how we use cookies, please contact our privacy team at privacy@bardplay.com. We're here to help you understand and manage your privacy choices.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="/privacy-policy"
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors duration-300 border border-white/20 inline-flex items-center justify-center gap-2"
                    >
                      <Icon name="Shield" size={20} />
                      View Privacy Policy
                    </a>
                    <a
                      href="/contact-us"
                      className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors duration-300 inline-flex items-center justify-center gap-2"
                    >
                      <Icon name="Mail" size={20} />
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CookiePolicy;